<template>
  <button
    ref="buttonElement"
    type="button"
    v-bind="$attrs"
    class="button-popout"
    @click="togglePopout($event)"
  >
    <slot name="selected" :value="value" :selected="true">
      <slot :value="value" :selected="true"></slot>
    </slot>
  </button>

  <floating-element
    class="button-popout__dialog"
    :visible="popout.visible"
    :x="popout.x"
    :y="popout.y"
    :x-alignment="xAlignment"
    :y-alignment="yAlignment"
    :non-closing-elements="nonClosingElements"
    @pointerdown:outside="popout.visible = false"
  >
    <template v-for="option in options" :key="option">
      <slot name="option" :value="option" :selected="option == value" :select="selectOption">
        <button
          type="button"
          class="button-popout__option"
          @click="selectOption($event, option)"
        >
          <slot :value="option" :selected="option == value"></slot>
        </button>
      </slot>
    </template>
  </floating-element>
</template>

<script setup lang="ts" generic="T extends string">
import { reactive, ref } from "vue";
import FloatingElement from "@core/ui/FloatingElement.vue";

defineOptions({
  inheritAttrs: false
});

const props = withDefaults(defineProps<{
  value: T;
  options: T[];
  xAlignment?: "left" | "right";
  yAlignment?: "top" | "bottom";
  nonClosingElements?: HTMLElement[];
}>(), {
  xAlignment: "right",
  yAlignment: "top",
  nonClosingElements: () => []
});

const emit = defineEmits<{
  (event: "update:value", value: T): void;
}>();

const buttonElement = ref<HTMLButtonElement | null>(null);

const popout = reactive({
  visible: false,
  x: 0,
  xOffset: 0,
  y: 0
});

function togglePopout(event: MouseEvent) {
  event.preventDefault();
  if (popout.visible) {
    popout.visible = false;
  } else {
    openPopout(event);
  }
}

function openPopout(event: MouseEvent) {
  popout.visible = true;
  if (buttonElement.value) {
    const bounding = buttonElement.value.getBoundingClientRect();
    if (props.xAlignment == "left") {
      popout.x = bounding.left;
    } else {
      popout.x = bounding.right;
    }
    if (props.yAlignment == "bottom") {
      popout.y = bounding.bottom;
    } else {
      popout.y = bounding.top;
    }
  } else {
    popout.x = event.clientX;
    popout.y = event.clientY;
  }
}

// ================================
// OPTIONS

function selectOption(event: MouseEvent, option: T) {
  event.preventDefault();
  popout.visible = false;
  emit("update:value", option);
}
</script>

<style lang="scss">
.button-popout {
  &__dialog {
    background-color: var(--color-background-close);
    display: flex;
    flex-direction: column;
    box-shadow: 0 2px 5px var(--color-shadow-2);
    overflow: hidden;
    border-radius: 3px;
    padding: 0.25rem 0;
  }

  &__option {
    background-color: var(--color-background-close);
    border: none;
    padding: 0.5rem 1rem;
    text-align: center;
  }
}
</style>
